/**
 * RepresentationCountrySelect
 * Searchable dropdown populated from /admin/representations/countries.
 * Works in two modes:
 *   - form mode (required=true): shows a placeholder and validates selection
 *   - filter mode (allowAll=true): includes an "All countries" option at the top
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { BsChevronDown, BsX, BsSearch } from 'react-icons/bs';
import representationService from '../api/representationService';

export default function RepresentationCountrySelect({
  value,
  onChange,
  placeholder = '— Sélectionner un pays —',
  allowAll = false,
  allLabel = 'Tous les pays',
  allowWorldwide = false,
  worldwideLabel = 'Monde entier',
  isInvalid = false,
  size,           // 'sm' | undefined
  disabled = false,
  width = null,
}) {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [triggerRect, setTriggerRect] = useState(null);
  const containerRef = useRef();
  const triggerRef = useRef();
  const searchRef = useRef();
  const debounceTimer = useRef(null);

  /* ── Load countries with optional search ── */
  const loadCountries = useCallback(async (searchQuery = '') => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      const { data } = await representationService.listCountries(params);
      setCountries(Array.isArray(data.data) ? data.data : []);
    } catch {
      setCountries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, []);

  // Initial load
  useEffect(() => { loadCountries(); }, [loadCountries]);

  // Debounced search when user types
  useEffect(() => {
    if (!open) {
      // Clear timer when dropdown closes
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      return;
    }
    
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      try {
        loadCountries(search);
      } catch (err) {
        console.debug('Countries load error:', err);
      }
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, [search, open, loadCountries]);

  /* ── Close on outside click ── */
  useEffect(() => {
    if (!open) return; // Only add listener when dropdown is open
    
    const handler = (e) => {
      try {
        // Check if click is outside both trigger and portal dropdown
        if (triggerRef.current && !triggerRef.current.contains(e.target)) {
          // Also check that click is not on the dropdown portal
          const dropdownElement = document.querySelector('[data-dropdown-portal="true"]');
          if (!dropdownElement || !dropdownElement.contains(e.target)) {
            setOpen(false);
            setSearch('');
          }
        }
      } catch (err) {
        console.debug('Dropdown click handler error:', err);
      }
    };
    
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  /* ── Focus search input when dropdown opens ── */
  useEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        try {
          searchRef.current?.focus();
        } catch (err) {
          console.debug('Search focus error:', err);
        }
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  /* ── Displayed label for the trigger ── */
  const selectedCountry = countries.find((c) => c.pays === value);
  const triggerLabel = value === 'worldwide'
    ? worldwideLabel
    : value
    ? value
    : (allowAll && !value)
      ? allLabel
      : placeholder;

  const height = size === 'sm' ? 31 : 38;

  const handleSelect = (pays) => {
    const country = countries.find((c) => c.pays === pays) || null;
    onChange(pays, country);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', null);
    setSearch('');
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    
    if (!open) {
      // Calculate trigger position for portal
      if (triggerRef.current) {
        try {
          const rect = triggerRef.current.getBoundingClientRect();
          setTriggerRect({
            top: rect.top + rect.height + 4,
            left: rect.left,
            width: rect.width,
          });
        } catch (err) {
          console.debug('Position calculation error:', err);
        }
      }
    }
    setOpen((v) => !v);
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', userSelect: 'none', width: width }}
    >
      {/* Trigger button */}
      <div
        ref={triggerRef}
        translate="no"
        onClick={handleTriggerClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height,
          padding: size === 'sm' ? '0 8px' : '0 12px',
          border: `1px solid ${isInvalid ? '#dc3545' : open ? '#8B0000' : '#ced4da'}`,
          borderRadius: 4,
          backgroundColor: disabled ? '#e9ecef' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: size === 'sm' ? 13 : 14,
          color: value ? '#212529' : '#6c757d',
          boxShadow: open ? '0 0 0 0.2rem rgba(139,0,0,.15)' : 'none',
          transition: 'border-color .15s, box-shadow .15s',
        }}
      >
        {/* Flag thumbnail */}
        {selectedCountry?.drapeau_url && (
          <img
            src={selectedCountry.drapeau_url}
            alt=""
            style={{ width: 22, height: 16, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
          />
        )}

        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {loading ? 'Chargement…' : triggerLabel}
        </span>

        {/* Clear button — only when a value is selected */}
        {value && !disabled && (
          <BsX
            size={16}
            color="#6c757d"
            style={{ flexShrink: 0, cursor: 'pointer' }}
            onMouseDown={handleClear}
          />
        )}

        {loading
          ? <Spinner size="sm" animation="border" style={{ color: '#8B0000', width: 14, height: 14 }} />
          : <BsChevronDown size={12} color="#6c757d" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        }
      </div>

      {/* Dropdown panel - rendered via Portal to avoid translation issues */}
      {open && triggerRect && createPortal(
        <div
          data-dropdown-portal="true"
          translate="no"
          style={{
            position: 'fixed',
            top: triggerRect.top,
            left: triggerRect.left,
            width: triggerRect.width,
            backgroundColor: '#fff',
            border: '1px solid #ced4da',
            borderRadius: 6,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            zIndex: 10050,
            maxHeight: 280,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <BsSearch size={13} color="#8B0000" style={{ flexShrink: 0 }} />
            <input
              ref={searchRef}
              type="text"
              translate="no"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un pays…"
              style={{
                border: 'none',
                outline: 'none',
                flex: 1,
                fontSize: 13,
                color: '#212529',
                background: 'transparent',
                minWidth: 0, // Allow input to shrink
                width: '100%', // Take full width of flex container
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setOpen(false); setSearch(''); }
                if (e.key === 'Enter' && countries.length === 1) handleSelect(countries[0].pays);
              }}
            />
          </div>

          {/* Options list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ padding: '20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Spinner size="sm" animation="border" style={{ color: '#8B0000' }} />
                <span style={{ fontSize: 13, color: '#6c757d' }}>Recherche en cours…</span>
              </div>
            ) : (
              <>
                {/* "Worldwide" option */}
                {allowWorldwide && (
                  <div
                    translate="no"
                    onClick={() => handleSelect('worldwide')}
                    style={{
                      padding: '7px 12px',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: value === 'worldwide' ? '#8B0000' : '#6c757d',
                      fontWeight: value === 'worldwide' ? 600 : 400,
                      backgroundColor: value === 'worldwide' ? '#fce8e8' : 'transparent',
                      borderBottom: '1px solid #f5f5f5',
                    }}
                    onMouseEnter={(e) => { if (value !== 'worldwide') e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                    onMouseLeave={(e) => { if (value !== 'worldwide') e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    🌍 {worldwideLabel}
                  </div>
                )}

                {/* "All" option */}
                {allowAll && (
                  <div
                    translate="no"
                    onClick={() => handleSelect('')}
                    style={{
                      padding: '7px 12px',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: !value ? '#8B0000' : '#6c757d',
                      fontStyle: 'italic',
                      fontWeight: !value ? 600 : 400,
                      backgroundColor: !value ? '#fce8e8' : 'transparent',
                      borderBottom: '1px solid #f5f5f5',
                    }}
                    onMouseEnter={(e) => { if (value) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                    onMouseLeave={(e) => { if (value) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {allLabel}
                  </div>
                )}

                {countries.length === 0 ? (
                  <div style={{ padding: '10px 12px', color: '#aaa', fontSize: 13, textAlign: 'center' }}>
                    Aucun pays trouvé
                  </div>
                ) : (
                  countries.map((c, idx) => {
                    const isSelected = c.pays === value;
                    return (
                      <div
                        key={`country-${c.id}-${idx}`}
                        translate="no"
                        onClick={() => handleSelect(c.pays)}
                        style={{
                          padding: '7px 12px',
                          cursor: 'pointer',
                          fontSize: 13,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          backgroundColor: isSelected ? '#fce8e8' : 'transparent',
                          color: isSelected ? '#8B0000' : '#212529',
                          fontWeight: isSelected ? 600 : 400,
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {c.drapeau_url && (
                          <img
                            src={c.drapeau_url}
                            alt=""
                            style={{ width: 22, height: 16, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
                          />
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.pays}</span>
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
